/**
 * @fileoverview Model preset definitions for bulk agent configuration.
 *
 * Each preset uses a layered approach:
 * - baseConfig: The "workhorse" model applied to all agents by default
 * - agentOverrides: Specific agents that need different models
 *
 * Presets are organized along two axes:
 * - Length: Fast / Short-form / Long-form / Epic
 * - Content: SFW / NSFW
 *
 * Key model assignment rules:
 * - Prose writing agents always use Claude Opus 4.5
 * - Judgment and planning agents always use Gemini models
 * - NSFW presets use Grok for base/character/extraction (uncensored)
 * - Long-form context window <= 250k tokens
 * - Epic context window >= 250k tokens
 */

import type { ModelPreset, PresetId } from './schema';

// =============================================================================
// SHARED AGENT OVERRIDE GROUPS
// =============================================================================

/** Prose writing agents → Claude Opus 4.5 */
const PROSE_OPUS = {
  'chapter-drafter-v2': {
    modelProvider: 'anthropic',
    modelName: 'claude-opus-4-5',
  },
  'prose-continuer-guided': {
    modelProvider: 'anthropic',
    modelName: 'claude-opus-4-5',
  },
  'prose-continuer-discovery': {
    modelProvider: 'anthropic',
    modelName: 'claude-opus-4-5',
  },
  'draft-refiner-v2': {
    modelProvider: 'anthropic',
    modelName: 'claude-opus-4-5',
  },
  'selection-reviser-v2': {
    modelProvider: 'anthropic',
    modelName: 'claude-opus-4-5',
  },
} as const;

/** Judgment agents → Gemini 3.1 Pro */
const JUDGMENT_PRO = {
  'draft-judge-v2': {
    modelProvider: 'google-ai',
    modelName: 'gemini-3.1-pro-preview',
  },
  'prose-comparer-v2': {
    modelProvider: 'google-ai',
    modelName: 'gemini-3.1-pro-preview',
  },
} as const;

/** Planning/architecture agents → Gemini 3.1 Pro */
const PLANNING_PRO = {
  'chapter-architect-v2': {
    modelProvider: 'google-ai',
    modelName: 'gemini-3.1-pro-preview',
  },
  'chapter-architect-remaining-v2': {
    modelProvider: 'google-ai',
    modelName: 'gemini-3.1-pro-preview',
  },
  'chapter-restructure-v1': {
    modelProvider: 'google-ai',
    modelName: 'gemini-3.1-pro-preview',
  },
} as const;

/** Style agents → Claude Opus 4.5 */
const STYLE_OPUS = {
  'style-analyzer-v2': {
    modelProvider: 'anthropic',
    modelName: 'claude-opus-4-5',
  },
  'generate-custom-style-guide-v2': {
    modelProvider: 'anthropic',
    modelName: 'claude-opus-4-5',
  },
} as const;

/**
 * Image creator agent groups.
 *
 * All image generation is routed through the FAL API regardless of provider
 * prefix — the model name is self-describing (e.g. xai/grok-imagine-image
 * tells FAL to route to xAI's Grok Imagine).
 *
 * Three distinct configurations:
 * - IMAGES_NANO_BANANA: Nano Banana for standard + multi-ref (default, all SFW except fast)
 * - IMAGES_GROK: Grok Imagine for standard + multi-ref (fast-sfw, all NSFW multi-ref)
 * - IMAGES_FAST_NSFW: Z-Image Turbo standard + Grok multi-ref (fast-nsfw, all other NSFW)
 */

/** 4 standard creators on Nano Banana, reference on Nano Banana/edit */
const IMAGES_NANO_BANANA = {
  'character-image-creator-fal-v2': {
    modelProvider: 'fal-ai',
    modelName: 'fal-ai/nano-banana',
  },
  'story-image-creator-fal-v2': {
    modelProvider: 'fal-ai',
    modelName: 'fal-ai/nano-banana',
  },
  'portrait-image-creator-fal-v2': {
    modelProvider: 'fal-ai',
    modelName: 'fal-ai/nano-banana',
  },
  'world-element-image-creator-fal-v2': {
    modelProvider: 'fal-ai',
    modelName: 'fal-ai/nano-banana',
  },
  'reference-image-creator-fal-v2': {
    modelProvider: 'fal-ai',
    modelName: 'fal-ai/nano-banana/edit',
  },
} as const;

/** 4 standard creators on Grok Imagine, reference on Grok Imagine/edit */
const IMAGES_GROK = {
  'character-image-creator-fal-v2': {
    modelProvider: 'xai',
    modelName: 'xai/grok-imagine-image',
  },
  'story-image-creator-fal-v2': {
    modelProvider: 'xai',
    modelName: 'xai/grok-imagine-image',
  },
  'portrait-image-creator-fal-v2': {
    modelProvider: 'xai',
    modelName: 'xai/grok-imagine-image',
  },
  'world-element-image-creator-fal-v2': {
    modelProvider: 'xai',
    modelName: 'xai/grok-imagine-image',
  },
  'reference-image-creator-fal-v2': {
    modelProvider: 'xai',
    modelName: 'xai/grok-imagine-image/edit',
  },
} as const;

/** 4 standard creators on Z-Image Turbo, reference on Grok Imagine/edit */
const IMAGES_ZIMAGE_TURBO = {
  'character-image-creator-fal-v2': {
    modelProvider: 'fal-ai',
    modelName: 'fal-ai/z-image/turbo',
  },
  'story-image-creator-fal-v2': {
    modelProvider: 'fal-ai',
    modelName: 'fal-ai/z-image/turbo',
  },
  'portrait-image-creator-fal-v2': {
    modelProvider: 'fal-ai',
    modelName: 'fal-ai/z-image/turbo',
  },
  'world-element-image-creator-fal-v2': {
    modelProvider: 'fal-ai',
    modelName: 'fal-ai/z-image/turbo',
  },
  'reference-image-creator-fal-v2': {
    modelProvider: 'xai',
    modelName: 'xai/grok-imagine-image/edit',
  },
} as const;

/** Character agents → Claude Opus 4.5 */
const CHARACTER_OPUS = {
  'character-arc-creation-v2': {
    modelProvider: 'anthropic',
    modelName: 'claude-opus-4-5',
  },
} as const;

/** Character agents → Grok 4 (NSFW) */
const CHARACTER_GROK = {
  'character-details-multimodal-generator-v3': {
    modelProvider: 'xai',
    modelName: 'grok-4',
  },
  'character-arc-creation-v2': {
    modelProvider: 'xai',
    modelName: 'grok-4',
  },
  'character-ideation-v2': {
    modelProvider: 'xai',
    modelName: 'grok-4',
  },
} as const;

/** Extraction agents → Grok 4 (NSFW) */
const EXTRACTION_GROK = {
  'chapter-extractor-v2': {
    modelProvider: 'xai',
    modelName: 'grok-4',
  },
  'character-extractor-full-v2': {
    modelProvider: 'xai',
    modelName: 'grok-4',
  },
  'plot-extractor-v2': {
    modelProvider: 'xai',
    modelName: 'grok-4',
  },
  'single-chapter-extractor-v2': {
    modelProvider: 'xai',
    modelName: 'grok-4',
  },
  'short-story-extractor': {
    modelProvider: 'xai',
    modelName: 'grok-4',
  },
  'story-dna-extractor-v2': {
    modelProvider: 'xai',
    modelName: 'grok-4',
  },
} as const;

// =============================================================================
// PRESET DEFINITIONS
// =============================================================================

/**
 * Default Preset (SFW, balanced)
 *
 * A reasonable mix for general use. Good prose quality with Opus,
 * strong planning/judgment on Gemini, affordable Flash base.
 */
const defaultPreset: ModelPreset = {
  id: 'default',
  name: 'Default',
  description: 'A balanced mix of quality and cost. Good for most SFW projects.',
  icon: 'Sliders',
  baseConfig: {
    modelProvider: 'google-ai',
    modelName: 'gemini-3-flash-preview',
  },
  agentOverrides: {
    ...IMAGES_NANO_BANANA,
    ...PROSE_OPUS,
    ...JUDGMENT_PRO,
    ...PLANNING_PRO,
    ...STYLE_OPUS,
    ...CHARACTER_OPUS,
  },
};

/**
 * Fast SFW Preset
 *
 * Maximum speed and minimum cost. Flash Lite for everything, no overrides.
 */
const fastSfwPreset: ModelPreset = {
  id: 'fast-sfw',
  name: 'Fast SFW',
  description: 'Maximum speed and minimum cost. Gemini Flash Lite for everything.',
  icon: 'Zap',
  baseConfig: {
    modelProvider: 'google-ai',
    modelName: 'gemini-3.1-flash-lite',
  },
  agentOverrides: {
    ...IMAGES_GROK,
  },
};

/**
 * Fast NSFW Preset
 *
 * Maximum speed for uncensored content. Grok 4.1 NoReason for everything.
 */
const fastNsfwPreset: ModelPreset = {
  id: 'fast-nsfw',
  name: 'Fast NSFW',
  description: 'Maximum speed for mature content. Grok 4.1 NoReason for everything.',
  icon: 'ZapOff',
  baseConfig: {
    modelProvider: 'xai',
    modelName: 'grok-4-1-fast-non-reasoning',
  },
  agentOverrides: {
    ...IMAGES_ZIMAGE_TURBO,
  },
};

/**
 * Short-form SFW Preset
 *
 * Optimized for flash fiction and short stories. Opus for prose,
 * minimal planning overhead since short works don't need it.
 */
const shortSfwPreset: ModelPreset = {
  id: 'short-sfw',
  name: 'Short-form SFW',
  description: 'For flash fiction and short stories. Premium prose, lightweight planning.',
  icon: 'FileText',
  baseConfig: {
    modelProvider: 'google-ai',
    modelName: 'gemini-3-flash-preview',
  },
  agentOverrides: {
    ...IMAGES_NANO_BANANA,
    ...PROSE_OPUS,
  },
};

/**
 * Short-form NSFW Preset
 *
 * Flash fiction and short stories with mature content. Grok base for
 * uncensored handling, Opus for prose quality.
 */
const shortNsfwPreset: ModelPreset = {
  id: 'short-nsfw',
  name: 'Short-form NSFW',
  description: 'For flash fiction and short stories with mature content.',
  icon: 'FileWarning',
  baseConfig: {
    modelProvider: 'xai',
    modelName: 'grok-4-1-fast-reasoning',
  },
  agentOverrides: {
    ...IMAGES_ZIMAGE_TURBO,
    ...PROSE_OPUS,
    ...CHARACTER_GROK,
  },
};

/**
 * Long-form SFW Preset
 *
 * For novelettes, novellas, and short novels (context <= 250k).
 * Strong planning and judgment on Gemini Pro, Opus for prose and style.
 */
const longSfwPreset: ModelPreset = {
  id: 'long-sfw',
  name: 'Long-form SFW',
  description: 'For novelettes and novellas. Strong planning and premium prose. Context <= 250k.',
  icon: 'BookOpen',
  baseConfig: {
    modelProvider: 'google-ai',
    modelName: 'gemini-3-flash-preview',
  },
  agentOverrides: {
    ...IMAGES_NANO_BANANA,
    ...PROSE_OPUS,
    ...JUDGMENT_PRO,
    ...PLANNING_PRO,
    ...STYLE_OPUS,
    ...CHARACTER_OPUS,
  },
};

/**
 * Long-form NSFW Preset
 *
 * For novelettes, novellas, and short novels with mature content (context <= 250k).
 * Grok 4 base for uncensored handling, Opus for prose, Gemini Pro for judgment/planning.
 */
const longNsfwPreset: ModelPreset = {
  id: 'long-nsfw',
  name: 'Long-form NSFW',
  description: 'For novelettes and novellas with mature content. Context <= 250k.',
  icon: 'BookLock',
  baseConfig: {
    modelProvider: 'xai',
    modelName: 'grok-4',
  },
  agentOverrides: {
    ...IMAGES_ZIMAGE_TURBO,
    ...PROSE_OPUS,
    ...JUDGMENT_PRO,
    ...PLANNING_PRO,
    ...STYLE_OPUS,
  },
};

/**
 * Epic SFW Preset
 *
 * For long novels and epics (context >= 250k). Premium everything.
 * Gemini Pro base for utility tasks with 1M context, Opus for prose/style/character.
 */
const epicSfwPreset: ModelPreset = {
  id: 'epic-sfw',
  name: 'Epic SFW',
  description: 'For long novels and epics. Maximum quality, large context >= 250k.',
  icon: 'Library',
  baseConfig: {
    modelProvider: 'google-ai',
    modelName: 'gemini-3.1-pro-preview',
  },
  agentOverrides: {
    ...IMAGES_NANO_BANANA,
    ...PROSE_OPUS,
    ...STYLE_OPUS,
    ...CHARACTER_OPUS,
  },
};

/**
 * Epic NSFW Preset
 *
 * For long novels and epics with mature content (context >= 250k).
 * Grok 4 base for uncensored handling, Opus for prose, Gemini Pro for judgment/planning.
 */
const epicNsfwPreset: ModelPreset = {
  id: 'epic-nsfw',
  name: 'Epic NSFW',
  description: 'For long novels and epics with mature content. Large context >= 250k.',
  icon: 'ShieldOff',
  baseConfig: {
    modelProvider: 'xai',
    modelName: 'grok-4',
  },
  agentOverrides: {
    ...IMAGES_ZIMAGE_TURBO,
    ...PROSE_OPUS,
    ...JUDGMENT_PRO,
    ...PLANNING_PRO,
    ...STYLE_OPUS,
    ...EXTRACTION_GROK,
  },
};

/**
 * All available presets indexed by ID.
 */
export const MODEL_PRESETS: Record<PresetId, ModelPreset> = {
  'default': defaultPreset,
  'fast-sfw': fastSfwPreset,
  'fast-nsfw': fastNsfwPreset,
  'short-sfw': shortSfwPreset,
  'short-nsfw': shortNsfwPreset,
  'long-sfw': longSfwPreset,
  'long-nsfw': longNsfwPreset,
  'epic-sfw': epicSfwPreset,
  'epic-nsfw': epicNsfwPreset,
};

/**
 * Ordered list of presets for UI display.
 */
export const PRESET_LIST: ModelPreset[] = [
  defaultPreset,
  fastSfwPreset,
  fastNsfwPreset,
  shortSfwPreset,
  shortNsfwPreset,
  longSfwPreset,
  longNsfwPreset,
  epicSfwPreset,
  epicNsfwPreset,
];

/**
 * Get a preset by ID.
 */
export function getPreset(id: PresetId): ModelPreset {
  return MODEL_PRESETS[id];
}
